/**
 * Toast manager utility for showing toasts from anywhere in the app
 * This allows us to show toasts from axios interceptors and other non-React contexts
 */

let toastFunction = null;

// Toast deduplication
const recentToasts = new Map();
const TOAST_DEDUP_WINDOW = 3000; // 3 seconds

/**
 * Register the toast function from the ToastProvider
 * This should be called when the ToastProvider is initialized
 */
export const registerToast = (toast) => {
  toastFunction = toast;
};

/**
 * Check if a toast message was recently shown
 * @param {string} message - The message to check
 * @returns {boolean} - True if message was recently shown
 */
const isRecentToast = (message) => {
  const now = Date.now();
  const lastShown = recentToasts.get(message);
  
  if (lastShown && (now - lastShown) < TOAST_DEDUP_WINDOW) {
    return true;
  }
  
  // Clean up old entries
  for (const [msg, timestamp] of recentToasts.entries()) {
    if (now - timestamp >= TOAST_DEDUP_WINDOW) {
      recentToasts.delete(msg);
    }
  }
  
  return false;
};

/**
 * Show a toast message
 * @param {string} message - The message to display
 * @param {Object} options - Toast options (duration, etc.)
 */
export const showToast = (message, options = {}) => {
  if (toastFunction) {
    toastFunction(message, options);
  } else {
    console.warn('Toast function not registered. Message:', message);
  }
};

/**
 * Show an error toast with default styling
 * @param {string} message - The error message to display
 */
export const showErrorToast = (message) => {
  // Check for duplicate error messages
  if (isRecentToast(message)) {
    return;
  }
  
  // Record this message as shown
  recentToasts.set(message, Date.now());
  
  showToast(message, { duration: 3000 });
};

/**
 * Show a success toast with default styling
 * @param {string} message - The success message to display
 */
export const showSuccessToast = (message) => {
  showToast(message, { duration: 3000 });
};
